import type { SpecType } from '@local/lib';

import { type StateCreator } from 'zustand';

import type { QualityFormulaStore } from '../qualityFormulaStore';

import { createDefaultSpecState } from '../../qualityFormulaDefaults';
import { SPEC_TYPES } from '../../qualityFormulaMetadata';

export interface SpecVisibilitySlice {
  /** Expands the given specs' cards — used when a test run is blocked by a missing/invalid field inside one. */
  expandSpecs: (specTypes: SpecType[]) => void;
  handleToggleCollapse: (specType: SpecType) => void;
  /**
   * The quality-type filter is the sole source of truth for which specs are enabled — selecting a
   * spec in Step 1 shows its card (collapsed by default) and its Step 2 actuals field; deselecting
   * (individually or via Clear) turns the card off and plays its exit transition. The formula
   * config itself is only reset to default when a spec is
   * (re)selected, not on removal — that way its last configuration is still there to render while
   * the card is fading out instead of flashing to blank defaults. Deselecting a spec also blanks
   * its Step 2 actual value and invalidates any Step 3 results computed while it was included (see
   * `testValuesSlice`/`runPreviewSlice`), since both must react to the same event together.
   */
  handleVisibleSpecTypesChange: (nextVisibleTypes: SpecType[]) => void;
  visibleSpecTypes: SpecType[];
}

export interface CreateSpecVisibilitySliceOptions {
  initialVisibleSpecTypes?: SpecType[];
}

/**
 * Owns which specs are visible/enabled and which are expanded — never edits an already-visible
 * spec's own formula fields beyond resetting a newly (re)selected spec to its defaults and
 * clearing a removed spec's `enabled` flag (see `specRowEditingSlice` for everything else). Shares
 * `specStates` with that slice without importing it — both reach it via `get()`/`set()` on the
 * combined store (see Zustand's slices pattern).
 */
export const createSpecVisibilitySlice =
  (options: CreateSpecVisibilitySliceOptions = {}): StateCreator<QualityFormulaStore, [], [], SpecVisibilitySlice> =>
  (set, get) => ({
    expandSpecs: specTypes => {
      set(state => {
        const specStates = { ...state.specStates };
        specTypes.forEach(specType => {
          specStates[specType] = { ...specStates[specType], collapsed: false };
        });
        return { specStates };
      });
    },

    handleToggleCollapse: specType => {
      if (!get().specStates[specType].enabled) return;

      set(state => ({
        specStates: {
          ...state.specStates,
          [specType]: { ...state.specStates[specType], collapsed: !state.specStates[specType].collapsed },
        },
      }));
    },

    handleVisibleSpecTypesChange: nextVisibleTypes => {
      const { visibleSpecTypes } = get();
      const nextVisibleSet = new Set(nextVisibleTypes);
      const removedTypes = visibleSpecTypes.filter(key => !nextVisibleTypes.includes(key));

      set(state => {
        const specStates = { ...state.specStates };
        SPEC_TYPES.forEach(spec => {
          const wasVisible = visibleSpecTypes.includes(spec.key);
          const isVisible = nextVisibleSet.has(spec.key);

          if (isVisible && !wasVisible) {
            specStates[spec.key] = { ...createDefaultSpecState(spec.key), collapsed: true, enabled: true };
          } else if (!isVisible && wasVisible) {
            specStates[spec.key] = { ...specStates[spec.key], enabled: false };
          }
        });

        return { specStates, visibleSpecTypes: nextVisibleTypes };
      });

      if (removedTypes.length > 0) {
        get().clearActualValues(removedTypes);
        get().handleClearResults();
      }
    },

    visibleSpecTypes: options.initialVisibleSpecTypes ?? [],
  });
